import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarOff, ChevronDown, LogOut, Mountain, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import AdminCalendar from "../components/AdminCalendar";

const TABS = ["all", "pending", "waivers", "confirmed", "cancelled"];

const badge = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  waivers_sent: "bg-sky-100 text-sky-800 border-sky-300",
  confirmed: "bg-olive-pale text-olive border-olive/40",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

const STATUS_LABEL = { waivers_sent: "waivers" };

function WaiverDetail({ booking }) {
  if (!booking) return <p className="text-base text-ink/50">Loading waivers…</p>;
  const waivers = booking.waivers || [];
  if (!waivers.length)
    return <p className="text-base text-ink/50">No waivers yet — approve the booking to send waiver links to every participant.</p>;
  return (
    <div>
      <p className="text-sm uppercase tracking-[0.2em] text-ink/50">Participant waivers</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {waivers.map((w) => (
          <div key={w.token} className="rounded-2xl border border-ink/15 bg-white p-5" data-testid={`waiver-card-${w.email}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-medium">{w.name}</p>
                <p className="text-sm text-ink/50">{w.email || "No email — covered by lead booker"}</p>
                {w.proxy && (
                  <p className="mt-0.5 text-sm font-medium text-terra">
                    Signed by lead booker{w.relationship ? ` · ${w.relationship}` : ""}
                  </p>
                )}
              </div>
              {w.signed ? (
                <span className="rounded-full border border-olive/40 bg-olive-pale px-3 py-1 text-sm text-olive">
                  Signed {new Date(w.signed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              ) : (
                <span className="rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-sm text-amber-800">
                  {w.proxy ? "Covered by lead's signature" : "Pending signature"}
                </span>
              )}
            </div>
            {w.signed && (
              <>
                <p className="mt-4 text-sm uppercase tracking-[0.2em] text-ink/45">Health disclosure</p>
                <p className="mt-1.5 rounded-xl bg-paper px-4 py-3 text-base leading-relaxed text-ink/75" data-testid={`waiver-health-${w.email}`}>
                  {w.health}
                </p>
                <p className="mt-4 text-sm uppercase tracking-[0.2em] text-ink/45">Signature</p>
                {w.signature_image ? (
                  <img src={w.signature_image} alt={`Signature of ${w.name}`} data-testid={`waiver-signature-${w.email}`}
                    className="mt-1.5 h-20 rounded-xl border border-ink/10 bg-white object-contain px-3 py-1" />
                ) : (
                  <p className="mt-1.5 font-serif text-2xl italic text-ink/80" data-testid={`waiver-signature-${w.email}`}>
                    {w.signature_text}
                  </p>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [tab, setTab] = useState("all");
  const [links, setLinks] = useState({});
  const [busy, setBusy] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockDate, setNewBlockDate] = useState("");
  const [blockSlot, setBlockSlot] = useState("full");
  const [slots, setSlots] = useState({});
  const [hikesMap, setHikesMap] = useState({});
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/bookings");
      setBookings(data);
    } catch {
      setBookings([]);
    }
    try {
      const { data } = await api.get("/availability");
      setBlockedDates(data.blocked || []);
    } catch {
      setBlockedDates([]);
    }
    try {
      const { data } = await api.get("/hikes");
      setHikesMap(Object.fromEntries(data.map((h) => [h.slug, parseInt(h.duration, 10) < 4 ? "half" : "full"])));
    } catch {}
  }, []);

  async function blockDate() {
    if (!newBlockDate) return;
    try {
      await api.post("/admin/availability", { date: newBlockDate, slot: blockSlot });
      toast.success(`${newBlockDate} blocked (${blockSlot === "am" ? "morning" : blockSlot === "pm" ? "afternoon" : "all day"})`);
      setNewBlockDate("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function unblockDate(date, slot) {
    try {
      await api.delete(`/admin/availability/${date}?slot=${slot}`);
      toast.success(`${date} is open again`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deleteBooking(id, name) {
    if (!window.confirm(`Delete the request from ${name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function nudgeWaivers(id) {
    setBusy(id + "remind");
    try {
      const { data } = await api.post(`/admin/bookings/${id}/remind`);
      toast.success(data.reminded > 0 ? `Reminder emailed to ${data.reminded} unsigned participant${data.reminded === 1 ? "" : "s"}` : "Everyone has already signed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  }

  const [periods, setPeriods] = useState([]);
  const [periodForm, setPeriodForm] = useState({ start: "", end: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ start: "", end: "" });

  const loadPeriods = useCallback(async () => {
    try {
      const { data } = await api.get("/availability");
      setPeriods(data.periods || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  async function addPeriod() {
    if (!periodForm.start || !periodForm.end) return;
    try {
      await api.post("/admin/periods", periodForm);
      toast.success("Availability period added");
      setPeriodForm({ start: "", end: "" });
      loadPeriods();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function savePeriod(id) {
    try {
      await api.put(`/admin/periods/${id}`, editForm);
      toast.success("Period updated");
      setEditId(null);
      loadPeriods();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function deletePeriod(id) {
    if (!window.confirm("Delete this availability period?")) return;
    try {
      await api.delete(`/admin/periods/${id}`);
      toast.success("Period removed");
      loadPeriods();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});

  async function toggleExpand(id) {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    if (!details[id]) {
      try {
        const { data } = await api.get(`/admin/bookings/${id}`);
        setDetails((d) => ({ ...d, [id]: data }));
      } catch (err) {
        toast.error(formatApiError(err));
      }
    }
  }

  useEffect(() => {
    document.title = "Bookings — Salt & Stone Admin";
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  async function setStatus(id, status) {
    setBusy(id + status);
    try {
      await api.patch(`/admin/bookings/${id}`, {
        status,
        payment_link: status === "confirmed" ? links[id] || null : null,
        slot:
          status === "confirmed"
            ? slots[id] ||
              ((bookings || []).find((b) => b.id === id)?.time_pref ??
                (hikesMap[(bookings || []).find((b) => b.id === id)?.hike_slug] === "half" ? "am" : "full"))
            : null,
      });
      toast.success(
        status === "waivers_sent"
          ? "Approved — waiver emails sent to all participants"
          : status === "confirmed"
            ? "Confirmed — email sent to guest"
            : "Booking cancelled — email sent"
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  }

  async function logout() {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  }

  if (!user) return <main className="min-h-screen bg-paper" data-testid="admin-loading" />;

  const filtered = (bookings || []).filter((b) => tab === "all" || (tab === "waivers" ? b.status === "waivers_sent" : b.status === tab));
  const counts = (bookings || []).reduce(
    (acc, b) => {
      const k = STATUS_LABEL[b.status] || b.status;
      return { ...acc, [k]: (acc[k] || 0) + 1 };
    },
    {}
  );

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-paper">
      <header className="border-b border-ink/10 bg-paper">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
          <div>
            <p className="font-serif text-2xl tracking-tight">
              Salt <span className="text-terra">&amp;</span> Stone
            </p>
            <p className="text-sm uppercase tracking-[0.2em] text-ink/45" data-testid="admin-welcome">
              Kalimera, {user.name || "Vasilis"}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Link to="/admin/hikes" data-testid="admin-hikes-link"
              className="flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm uppercase tracking-[0.15em] text-ink/70 transition-colors duration-300 hover:border-terra hover:text-terra">
              <Mountain size={13} /> Edit hikes
            </Link>
            <button onClick={load} data-testid="admin-refresh-btn"
              className="flex items-center gap-2 rounded-full border border-ink/20 px-4 py-2 text-sm uppercase tracking-[0.15em] text-ink/70 transition-colors duration-300 hover:border-terra hover:text-terra">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm uppercase tracking-[0.15em] text-paper transition-colors duration-300 hover:bg-terra">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-status-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
              className={`rounded-full border px-5 py-2 text-sm uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t ? "border-ink bg-ink text-paper" : "border-ink/20 text-ink/60 hover:border-ink/50"
              }`}>
              {t}{t !== "all" && counts[t] ? ` · ${counts[t]}` : ""}
            </button>
          ))}
        </div>

        {!bookings ? (
          <p className="mt-14 text-ink/50">Loading requests…</p>
        ) : filtered.length === 0 ? (
          <p className="mt-14 text-ink/50" data-testid="admin-empty">No {tab === "all" ? "" : tab} booking requests yet.</p>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-ink/15" data-testid="admin-bookings-table">
            <table className="w-full min-w-[860px] border-collapse text-left text-base">
              <thead>
                <tr className="border-b border-ink/15 bg-ink/[0.03] text-sm uppercase tracking-[0.18em] text-ink/50">
                  <th className="px-4 py-3 font-medium">Guest</th>
                  <th className="px-4 py-3 font-medium">Hike</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">People</th>
                  <th className="px-4 py-3 font-medium">Message</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <Fragment key={b.id}>
                  <tr className="border-b border-ink/10 align-top transition-colors duration-200 hover:bg-olive-pale/40"
                    data-testid={`admin-booking-row-${b.id.slice(0, 8)}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2.5">
                        <button onClick={() => toggleExpand(b.id)} data-testid={`admin-expand-${b.id.slice(0, 8)}`}
                          aria-label={`Show waivers for ${b.name}`}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink/45 transition-colors duration-300 hover:border-terra hover:text-terra">
                          <ChevronDown size={14} className={`transition-transform duration-300 ${expandedId === b.id ? "rotate-180" : ""}`} />
                        </button>
                        <div>
                          <p className="font-medium">{b.name}</p>
                          <p className="text-sm text-ink/50">{b.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">{b.hike_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {b.preferred_date}
                      {b.time_pref && (
                        <span className="mt-0.5 block text-sm text-ink/50">
                          {b.time_pref === "am" ? "morning" : "afternoon"} preferred
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">{b.people}</td>
                    <td className="max-w-[220px] px-4 py-4 text-sm leading-relaxed text-ink/60">{b.message || "—"}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-block rounded-full border px-3 py-1 text-sm uppercase tracking-[0.12em] ${badge[b.status]}`}
                        data-testid={`admin-status-${b.id.slice(0, 8)}`}>
                        {STATUS_LABEL[b.status] || b.status}
                      </span>
                      {b.status === "waivers_sent" && (b.waivers || []).length > 0 && (
                        <span className="mt-1.5 block text-sm text-ink/50" data-testid={`admin-waiver-progress-${b.id.slice(0, 8)}`}>
                          {(b.waivers || []).filter((w) => w.signed).length}/{(b.waivers || []).length} waivers signed
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {b.status === "pending" && (
                        <div className="flex min-w-[240px] flex-col gap-2">
                          <input
                            type="url"
                            placeholder="Payment link (optional, https://…)"
                            value={links[b.id] || ""}
                            onChange={(e) => setLinks({ ...links, [b.id]: e.target.value })}
                            data-testid={`admin-payment-link-${b.id.slice(0, 8)}`}
                            className="w-full border border-ink/20 bg-white px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-terra"
                          />
                          {hikesMap[b.hike_slug] === "half" && (
                            <select
                              value={slots[b.id] || b.time_pref || "am"}
                              onChange={(e) => setSlots({ ...slots, [b.id]: e.target.value })}
                              data-testid={`block-slot-${b.id.slice(0, 8)}`}
                              className="w-full rounded-lg border border-ink/20 bg-white px-3 py-2 text-sm outline-none transition-colors duration-300 focus:border-terra"
                            >
                              <option value="am">Blocks: morning only</option>
                              <option value="pm">Blocks: afternoon only</option>
                              <option value="full">Blocks: all day</option>
                            </select>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <button onClick={() => setStatus(b.id, "waivers_sent")} disabled={!!busy}
                              data-testid={`admin-confirm-${b.id.slice(0, 8)}`}
                              className="whitespace-nowrap rounded-full bg-olive px-4 py-2 text-sm uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-olive/80 disabled:opacity-50">
                              {busy === b.id + "waivers_sent" ? "…" : "Approve & send waivers"}
                            </button>
                            <button onClick={() => setStatus(b.id, "cancelled")} disabled={!!busy}
                              data-testid={`admin-cancel-${b.id.slice(0, 8)}`}
                              className="rounded-full border border-red-300 px-4 py-2 text-sm uppercase tracking-[0.12em] text-red-700 transition-colors duration-300 hover:bg-red-50 disabled:opacity-50">
                              Cancel
                            </button>
                            <button onClick={() => deleteBooking(b.id, b.name)} disabled={!!busy}
                              data-testid={`admin-delete-${b.id.slice(0, 8)}`} aria-label={`Delete booking from ${b.name}`}
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/40 transition-colors duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                      {b.status !== "pending" && (
                        <div className="flex flex-wrap items-center gap-2">
                          {b.status === "waivers_sent" && (
                            <button onClick={() => nudgeWaivers(b.id)} disabled={!!busy}
                              data-testid={`admin-nudge-${b.id.slice(0, 8)}`}
                              className="whitespace-nowrap rounded-full bg-sky-600 px-4 py-2 text-sm uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-sky-700 disabled:opacity-50">
                              {busy === b.id + "remind" ? "…" : "Nudge unsigned"}
                            </button>
                          )}
                          {(b.status === "confirmed" || b.status === "waivers_sent") && (
                            <button onClick={() => setStatus(b.id, "cancelled")} disabled={!!busy}
                              data-testid={`admin-cancel-${b.id.slice(0, 8)}`}
                              className="whitespace-nowrap rounded-full border border-red-300 px-4 py-2 text-sm uppercase tracking-[0.12em] text-red-700 transition-colors duration-300 hover:bg-red-50 disabled:opacity-50">
                              Cancel
                            </button>
                          )}
                          <button onClick={() => deleteBooking(b.id, b.name)} disabled={!!busy}
                            data-testid={`admin-delete-${b.id.slice(0, 8)}`} aria-label={`Delete booking from ${b.name}`}
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-ink/15 text-ink/40 transition-colors duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedId === b.id && (
                    <tr data-testid={`admin-detail-${b.id.slice(0, 8)}`}>
                      <td colSpan={7} className="border-b border-ink/10 bg-paper px-5 py-6">
                        <WaiverDetail booking={details[b.id]} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <AdminCalendar
          bookings={bookings || []}
          blockedDates={blockedDates}
          periods={periods}
          onPickDate={(d) => {
            setNewBlockDate(d);
            document.querySelector('[data-testid="admin-availability"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        />

        <section className="mt-12 rounded-2xl border border-ink/15 bg-white p-6 sm:p-8" data-testid="admin-availability">
          <div className="flex items-center gap-3">
            <CalendarOff size={18} className="text-terra" />
            <h2 className="font-serif text-2xl tracking-tight">Availability</h2>
          </div>
          <p className="mt-2 text-base leading-relaxed text-ink/55">
            Blocked days can't be requested by guests. Confirming a booking blocks its date automatically;
            cancelling frees it again.
          </p>

          <div className="mt-7 rounded-xl border border-ink/10 bg-paper p-5" data-testid="periods-manager">
            <h3 className="text-sm uppercase tracking-[0.2em] text-ink/50">Operating periods</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink/50">
              Guests can only request dates inside these ranges. Leave the list empty to accept any date.
            </p>
            {periods.map((p) => (
              <div key={p.id} className="mt-3 flex flex-wrap items-center gap-3" data-testid={`period-row-${p.id.slice(0, 8)}`}>
                {editId === p.id ? (
                  <>
                    <input type="date" value={editForm.start} onChange={(e) => setEditForm({ ...editForm, start: e.target.value })}
                      data-testid="period-edit-start"
                      className="rounded-xl border border-ink/20 bg-white px-4 py-2 text-base outline-none transition-colors duration-300 focus:border-terra" />
                    <span className="text-ink/40">→</span>
                    <input type="date" value={editForm.end} onChange={(e) => setEditForm({ ...editForm, end: e.target.value })}
                      data-testid="period-edit-end"
                      className="rounded-xl border border-ink/20 bg-white px-4 py-2 text-base outline-none transition-colors duration-300 focus:border-terra" />
                    <button onClick={() => savePeriod(p.id)} data-testid="period-save"
                      className="rounded-full bg-olive px-4 py-2 text-sm uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-olive/80">
                      Save
                    </button>
                    <button onClick={() => setEditId(null)} className="text-sm text-ink/50 transition-colors duration-300 hover:text-ink">
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <span className="rounded-full bg-olive-pale px-4 py-1.5 text-sm text-olive">{p.start} → {p.end}</span>
                    <button onClick={() => { setEditId(p.id); setEditForm({ start: p.start, end: p.end }); }}
                      data-testid={`period-edit-${p.id.slice(0, 8)}`}
                      className="text-sm uppercase tracking-[0.12em] text-ink/50 transition-colors duration-300 hover:text-terra">
                      Edit
                    </button>
                    <button onClick={() => deletePeriod(p.id)} data-testid={`period-delete-${p.id.slice(0, 8)}`} aria-label="Delete period"
                      className="flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 text-ink/40 transition-colors duration-300 hover:border-red-300 hover:bg-red-50 hover:text-red-600">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            ))}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input type="date" value={periodForm.start} onChange={(e) => setPeriodForm({ ...periodForm, start: e.target.value })}
                data-testid="period-start-input" aria-label="Period start date"
                className="rounded-xl border border-ink/20 bg-white px-4 py-2 text-base outline-none transition-colors duration-300 focus:border-terra" />
              <span className="text-ink/40">→</span>
              <input type="date" value={periodForm.end} onChange={(e) => setPeriodForm({ ...periodForm, end: e.target.value })}
                data-testid="period-end-input" aria-label="Period end date"
                className="rounded-xl border border-ink/20 bg-white px-4 py-2 text-base outline-none transition-colors duration-300 focus:border-terra" />
              <button onClick={addPeriod} data-testid="period-add-btn"
                className="flex items-center gap-2 rounded-full bg-terra px-5 py-2.5 text-sm uppercase tracking-[0.12em] text-paper transition-colors duration-300 hover:bg-terra-dark">
                <Plus size={13} /> Add period
              </button>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <input
              type="date"
              value={newBlockDate}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setNewBlockDate(e.target.value)}
              data-testid="block-date-input"
              className="rounded-xl border border-ink/20 bg-white px-4 py-2.5 text-base outline-none transition-colors duration-300 focus:border-terra"
            />
            <select
              value={blockSlot}
              onChange={(e) => setBlockSlot(e.target.value)}
              data-testid="block-slot-select"
              className="rounded-xl border border-ink/20 bg-white px-4 py-2.5 text-base outline-none transition-colors duration-300 focus:border-terra"
            >
              <option value="full">All day</option>
              <option value="am">Morning only</option>
              <option value="pm">Afternoon only</option>
            </select>
            <button onClick={blockDate} data-testid="block-date-btn"
              className="flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-sm uppercase tracking-[0.15em] text-paper transition-colors duration-300 hover:bg-terra">
              <Plus size={13} /> Block this date
            </button>
          </div>
          {blockedDates.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="blocked-dates-list">
              {blockedDates.map((e) => (
                <span key={`${e.date}-${e.slot}`} className="flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-1.5 text-sm text-red-700">
                  {e.date} · {e.slot === "am" ? "morning" : e.slot === "pm" ? "afternoon" : "all day"}
                  <button onClick={() => unblockDate(e.date, e.slot)} data-testid={`unblock-${e.date}-${e.slot}`} aria-label={`Unblock ${e.date}`}
                    className="text-red-400 transition-colors duration-300 hover:text-red-700">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>

        <p className="mt-6 text-sm text-ink/40">
          Approving sends a personal waiver link to every participant. When all have signed, the booking becomes
          confirmed and the guest receives the confirmation email — with your payment link if you pasted one,
          otherwise cash-on-the-day instructions.
        </p>
      </div>
    </main>
  );
}
